import type { EF } from "@portal/@types/everflow";
import { getSafeUrlParam } from "./urlParamUtils";

type ClickOptions = Parameters<EF["click"]>[0];
type EverflowParameterDefinition = {
  startsNewClick: boolean;
  platform?: string | ((value: string) => string);
} & (
  | { trackingField: `everflow${string}`; sdkOption: keyof ClickOptions }
  // null preserves the SDK's automatic handling of landing_url. Captured
  // fields above must choose a transport so an addition cannot be stored only.
  | { trackingField?: never; sdkOption: keyof ClickOptions | null }
);

/**
 * Register supported landing parameters here. Capture, new-arrival detection,
 * SDK options, and generic server sub mappings derive from this definition.
 * A new field explicitly chooses whether it starts a click and how it reaches
 * the SDK. Unregistered parameters (including UTMs) retain existing behavior.
 */
export const EVERFLOW_PARAMETER_DEFINITIONS = {
  oid: { trackingField: "everflowOfferId", startsNewClick: true, sdkOption: "offer_id" },
  // uid selects the offer's destination URL, not the offer itself. Our offer
  // is always 1, so its destination URL can identify the individual campaign.
  uid: { trackingField: "everflowOfferUrlId", startsNewClick: true, sdkOption: "uid" },
  affid: { trackingField: "everflowAffiliateId", startsNewClick: true, sdkOption: "affiliate_id" },
  source_id: { startsNewClick: true, sdkOption: "source_id" },
  sub1: { trackingField: "everflowSub1", startsNewClick: true, sdkOption: "sub1" },
  sub2: { trackingField: "everflowSub2", startsNewClick: true, sdkOption: "sub2" },
  sub3: { trackingField: "everflowSub3", startsNewClick: true, sdkOption: "sub3" },
  sub4: { trackingField: "everflowSub4", startsNewClick: true, sdkOption: "sub4" },
  sub5: { trackingField: "everflowSub5", startsNewClick: true, sdkOption: "sub5" },
  // SDK 1.5 has named options for sub1-5; additional subs use its generic map.
  // Meta uses stable IDs here: sub6 = ad, sub7 = ad set, sub8 = campaign.
  sub6: { trackingField: "everflowSub6", startsNewClick: true, sdkOption: "parameters" },
  sub7: { trackingField: "everflowSub7", startsNewClick: true, sdkOption: "parameters" },
  sub8: { trackingField: "everflowSub8", startsNewClick: true, sdkOption: "parameters" },
  // Preserve SDK ad-ID fallback and platform precedence using the frozen URL.
  fbclid: {
    startsNewClick: true,
    sdkOption: null,
    platform: (value: string) => (value.startsWith("PAA") ? "Instagram" : "Facebook"),
  },
  gclid: { startsNewClick: true, sdkOption: null, platform: "Google" },
  ttclid: { startsNewClick: true, sdkOption: null, platform: "Tiktok" },
  ScCid: { startsNewClick: true, sdkOption: null, platform: "Snapchat" },
  sccid: { startsNewClick: true, sdkOption: null, platform: "Snapchat" },
  MSCLKID: { startsNewClick: true, sdkOption: null, platform: "Microsoft" },
  msclkid: { startsNewClick: true, sdkOption: null, platform: "Microsoft" },
  OutbrainClickId: { startsNewClick: true, sdkOption: null, platform: "Outbrain" },
  TCLID: { startsNewClick: true, sdkOption: null, platform: "Taboola" },
} as const satisfies Record<string, EverflowParameterDefinition>;

type Definitions = typeof EVERFLOW_PARAMETER_DEFINITIONS;
type AffiliateParameterEntry = {
  [Key in keyof Definitions]: Definitions[Key] extends { trackingField: infer Field }
    ? readonly [Key, Field]
    : never;
}[keyof Definitions];

// Object.entries loses literal key/value pairs; restore them at this boundary.
export const EVERFLOW_AFFILIATE_PARAMETER_ENTRIES = Object.entries(
  EVERFLOW_PARAMETER_DEFINITIONS
).flatMap(([key, definition]) =>
  "trackingField" in definition ? [[key, definition.trackingField] as const] : []
) as readonly AffiliateParameterEntry[];

export type EverflowAffiliateParams = Partial<Record<AffiliateParameterEntry[1], string>>;

/** Project registered fields without changing each caller's null/empty handling. */
export function mapEverflowAffiliateParams<Value>(
  getValue: (field: keyof EverflowAffiliateParams, urlKey: AffiliateParameterEntry[0]) => Value
): Record<keyof EverflowAffiliateParams, Value> {
  return Object.fromEntries(
    EVERFLOW_AFFILIATE_PARAMETER_ENTRIES.map(([urlKey, field]) => [field, getValue(field, urlKey)])
  ) as Record<keyof EverflowAffiliateParams, Value>;
}

type SubParameterEntry = Extract<AffiliateParameterEntry, readonly [`sub${number}`, string]>;
export const EVERFLOW_SUB_PARAMETER_ENTRIES = EVERFLOW_AFFILIATE_PARAMETER_ENTRIES.filter(
  (entry): entry is SubParameterEntry => /^sub[1-9]\d*$/.test(entry[0])
);
export type EverflowSubParams = Partial<Record<SubParameterEntry[0], string>>;

export function hasEverflowCampaignParameters(search: string): boolean {
  return Object.entries(EVERFLOW_PARAMETER_DEFINITIONS).some(
    ([key, definition]) => definition.startsNewClick && !!getSafeUrlParam(key, search)
  );
}
